import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useAuth } from "contexts";
import { UserType, UserRole } from "types/Auth/AuthTypes";
import { generateExcerpt, listAll } from "services";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

interface CourseManagementContextType {
  institutionId: string | null;
  isLoading: boolean;
  error: Error | null;
  fetchCourse: (id: string) => Promise<any>;
  fetchCourseList: (
    limit?: number,
    onPage?: (accumulated: any[]) => void,
  ) => Promise<any>;
  addCourse: (courseData: any) => Promise<any>;
  editCourse: (id: string, courseData: any) => Promise<any>;
  removeCourse: (ids: string[]) => Promise<any>;
  generateExcerpt: (content: string, tags: string[]) => string;
}

const CourseManagementContext = createContext<
  CourseManagementContextType | undefined
>(undefined);

export const useCourseManagement = () => {
  const context = useContext(CourseManagementContext);
  if (context === undefined) {
    throw new Error(
      "useCourseManagement must be used within a CourseManagementProvider",
    );
  }
  return context;
};

interface CourseManagementProviderProps {
  children: ReactNode;
}

export const CourseManagementProvider: React.FC<
  CourseManagementProviderProps
> = ({ children }) => {
  const { authState } = useAuth();
  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchInstitutionId() {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;

        if (idToken) {
          const customDomain = idToken.payload["custom:domain"] as string;

          setInstitutionId(customDomain);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch institution ID"),
        );
      } finally {
        setIsLoading(false);
      }
    }

    fetchInstitutionId();
  }, []);

  async function fetchCourse(id: string) {
    setIsLoading(true);
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    try {
      const authMode = authState.isSignedIn ? "userPool" : "iam";

      const response = await client.models.Course.get(
        {
          courseId: id,
        },
        {
          authMode: authMode as any,
          authToken: token,
        },
      );

      if (response.data) {
        return response.data;
      }
    } catch (error) {
      setError(
        error instanceof Error ? error : new Error("Failed to fetch course"),
      );
    } finally {
      setIsLoading(false);
    }
    return null;
  }

  // DynamoDB caps each response at 1MB regardless of `limit`, so a single
  // .list() call can silently return only a partial page once the data
  // exceeds that cap. listAll loops internally with nextToken until every
  // matching course has been fetched, so callers always get the complete
  // list in one await — no caller-side looping needed.
  // Pass onPage to receive accumulated results after each page loads, for
  // progressive rendering while the rest streams in.
  async function fetchCourseList(
    limit: number = 1000,
    onPage?: (accumulated: any[]) => void,
  ) {
    const isSuperAdmin =
      authState.userType === UserType.EDUBAY &&
      authState.role &&
      [UserRole.SUPERADMIN, UserRole.OWNER].includes(authState.role);

    if (!institutionId && !isSuperAdmin) return null;

    setIsLoading(true);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const baseOptions: any = {
        authMode: "userPool",
        authToken: token,
        limit,
      };

      if (!isSuperAdmin && institutionId) {
        baseOptions.filter = {
          institutionId: { eq: institutionId },
        };
      }

      const allData = await listAll(
        (nextToken) =>
          client.models.Course.list({
            ...baseOptions,
            ...(nextToken ? { nextToken } : {}),
          }),
        onPage,
      );

      return { items: allData };
    } catch (error) {
      setError(
        error instanceof Error ? error : new Error("Failed to fetch courses"),
      );
    } finally {
      setIsLoading(false);
    }
    return null;
  }

  async function addCourse(courseData: any) {
    setIsLoading(true);
    try {
      const response = await client.models.Course.create({
        courseId: courseData.courseId,
        institutionId: courseData.institutionId,
        institutionName: courseData.institutionName,
        courseName: courseData.courseName,
        level: courseData.level,
        fieldOfStudy: courseData.fieldOfStudy,
        courseCode: courseData.courseCode,
        description: courseData.description,
        careerProspect: courseData.careerProspect,
        modeOfStudy: courseData.modeOfStudy,
        programType: courseData.programType,
        address: courseData.address,
        geoPoint: courseData.geoPoint,
        startDate: courseData.startDate,
        durationDay: courseData.durationDay,
        tuitionFee: courseData.tuitionFee,
        nonTuitionFee: courseData.nonTuitionFee,
        estimatedTotalCourseFee: courseData.estimatedTotalCourseFee,
        languageTests: courseData.languageTests,
        country: courseData.country,
        url: courseData.url,
        courseVector: courseData.courseVector,
      });

      if (response.errors && response.errors.length > 0) {
        const errorMsg = response.errors[0].message || "Failed to add course";
        console.error("Course creation errors:", response.errors);
        throw new Error(errorMsg);
      }

      return response.data;
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error("Failed to add course");
      setError(err);
      throw err; // Re-throw the error
    } finally {
      setIsLoading(false);
    }
  }

  async function editCourse(id: string, courseData: any) {
    setIsLoading(true);
    try {
      const response = await client.models.Course.update({
        courseId: id,
        institutionName: courseData.institutionName,
        courseName: courseData.courseName,
        level: courseData.level,
        fieldOfStudy: courseData.fieldOfStudy,
        courseCode: courseData.courseCode,
        description: courseData.description,
        careerProspect: courseData.careerProspect,
        modeOfStudy: courseData.modeOfStudy,
        programType: courseData.programType,
        address: courseData.address,
        geoPoint: courseData.geoPoint,
        startDate: courseData.startDate,
        durationDay: courseData.durationDay,
        tuitionFee: courseData.tuitionFee,
        nonTuitionFee: courseData.nonTuitionFee,
        estimatedTotalCourseFee: courseData.estimatedTotalCourseFee,
        languageTests: courseData.languageTests,
        country: courseData.country,
        url: courseData.url,
      });

      // Check for errors in response
      if (response.errors && response.errors.length > 0) {
        const errorMsg =
          response.errors[0].message || "Failed to update course";
        console.error("Course update errors:", response.errors);
        throw new Error(errorMsg);
      }

      return response.data;
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error("Failed to update course");
      setError(err);
      throw err; // Re-throw the error
    } finally {
      setIsLoading(false);
    }
  }

  async function removeCourse(ids: string[]) {
    setIsLoading(true);
    try {
      const deletePromises = ids.map((id) =>
        client.models.Course.delete({ courseId: id }),
      );

      const results = await Promise.all(deletePromises);
      return results;
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error("Failed to delete courses");
      setError(err);
      throw err; // Re-throw the error
    } finally {
      setIsLoading(false);
    }
  }

  const value = {
    institutionId,
    isLoading,
    error,
    fetchCourse,
    fetchCourseList,
    addCourse,
    editCourse,
    removeCourse,
    generateExcerpt,
  };

  return (
    <CourseManagementContext.Provider value={value}>
      {children}
    </CourseManagementContext.Provider>
  );
};

export default CourseManagementProvider;
